import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api'
import type { AdminUser } from '@/api'
import {
  UNAUTHORIZED_EVENT,
  clearAdminToken,
  getAdminToken,
  isUnauthorizedFor,
  setAdminToken,
} from '@/api/session'

/**
 * 当前**后台账号**（`/admin` 那一侧）。
 *
 * ### 与 `AuthContext` 是两件事，不是一个东西的两个名字
 *
 * 拆表（2026-10-07）之后会员与后台是**两套身份、两套登录、两个 token**：
 *
 * | | 会员（`AuthContext`） | 后台（本文件） |
 * | --- | --- | --- |
 * | 端点 | `/api/auth/*` | `/api/admin/auth/*` |
 * | token 键 | `hw.token` | `hw.adminToken` |
 * | 账号字段 | 有 `coins` / `plan` | **有 `lastLoginAt`，没有额度** |
 * | 未登录时 | 回工作台登录页 | `/admin` 原地给后台登录页 |
 *
 * 所以「两者都在」是合法状态：同一个浏览器可以既登着工作台、又登着后台。
 * 用一个 Context 管两套 token 的话，登录后台会顶掉工作台，
 * 而用户只会看到「我去后台看一眼，回来怎么要重新登录」。
 *
 * ### 这里**没有** `isAdmin`
 *
 * 能进到这个 Provider 里的，只有「后台会话已建立」和「还没建立」两种状态 ——
 * 没有「是会员但不是管理员」这种中间态（那种人会停在后台登录页上）。
 * 再挂一个恒为 true 的 `isAdmin` 只会让人以为这里也管角色判断。
 *
 * ### 401 必须按身份过滤
 *
 * `client.ts` 的广播带身份（见 `api/session.ts`）。不过滤的后果是：
 * 后台 token 过期 → 把工作台也清掉；工作台 token 过期 → 把后台也清掉。
 * 两种都是一次「毫无理由的掉线」。
 */

type AdminApi = {
  /** 当前后台账号。null = 未登录（或后台会话已失效）。 */
  admin: AdminUser | null
  /** 首次进入还在验会话。true 时不要渲染登录页 —— 那会闪一下。 */
  booting: boolean
  /**
   * 后台登录。**成功即写入后台 token**（与会员那份互不影响）。
   *
   * ⚠️ 失败时**一个字节都不写状态**。旧版的做法是「先 setUser 进来、再 logout
   * 出去」—— 于是账号中途非空了一瞬，门卫当场渲染了一次后台页，登录页整个
   * 被卸载重挂，那条错误提示落在一个已经死掉的实例上（React 对已卸载组件
   * setState 是 no-op）。用户看到的是：闪一下后台 → 回到登录页 → **一个字的原因都没有**。
   */
  login: (account: string, password: string) => Promise<AdminUser>
  /** 退出后台。**只吊销后台 token**，工作台不受影响。 */
  logout: () => Promise<void>
}

const Ctx = createContext<AdminApi | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [booting, setBooting] = useState(true)

  useEffect(() => {
    let alive = true
    if (!getAdminToken()) {
      // 没带后台 token —— 不必问后端，省一次注定 401 的请求
      setBooting(false)
      return
    }
    api
      .adminSession()
      .then((a) => {
        if (alive) setAdmin(a)
      })
      .catch(() => {
        // 401 / 403（过期、被停用）都当未登录。不弹错：「要重新登录」不是错误。
        if (alive) setAdmin(null)
      })
      .finally(() => {
        if (alive) setBooting(false)
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    const onUnauthorized = (e: Event) => {
      if (isUnauthorizedFor(e, 'admin')) setAdmin(null)
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const login = useCallback(async (account: string, password: string) => {
    const res = await api.adminLogin(account, password)
    setAdminToken(res.token)
    setAdmin(res.admin)
    return res.admin
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.adminLogout()
    } catch {
      // 后端挂了 / 已过期：本地照样要退出
    }
    clearAdminToken()
    setAdmin(null)
  }, [])

  const value = useMemo<AdminApi>(() => ({ admin, booting, login, logout }), [
    admin,
    booting,
    login,
    logout,
  ])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAdminAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider（它只挂在 /admin 子树）')
  }
  return ctx
}
